// src/components/account/ResetPasswordClient.tsx
"use client";

import React, { useMemo, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import SafeHtml from "@/components/ui/SafeHtml";
import OtpInput from "@/components/ui/OtpInput";
import Section from "../ui/Section";
import Wrapper from "../ui/Wrapper";
import { useLayoutData } from "../layout/LayoutDataProvider";
import { apiPath } from "@/lib/apiPath";

const OTP_LEN = 6;

export default function ResetPasswordClient() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { templateRoutes, global_text } = useLayoutData();
  const loginHref = (templateRoutes ?? {})["page-login.php"];

  const RESET: Record<string, string> =
    (global_text as { auth?: { reset_password?: Record<string, string> } } | undefined)
      ?.auth?.reset_password ?? {};
  const t = (k: string, fallback = "") => RESET[k] || fallback;

  const rid = useMemo(() => searchParams.get("rid") ?? "", [searchParams]);

  const [step, setStep] = useState<"code" | "password">("code");
  const [otp, setOtp] = useState<string[]>(Array(OTP_LEN).fill(""));
  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);

  function getCode() {
    return otp.join("");
  }

  function clearOtp() {
    setOtp(Array(OTP_LEN).fill(""));
  }

  function goNextFromCode(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setCodeError(null);
    const code = getCode();
    if (!rid) return setCodeError(t("error_rid_missing", "Reset request ID missing. Please use the link from your email."));
    if (code.length !== OTP_LEN || otp.some((d) => d === "")) return setCodeError(t("error_code_incomplete", "The code must be 6 digits."));
    setStep("password");
  }

  async function submitPassword(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setCodeError(null);
    const code = getCode();
    if (!rid) return setError(t("error_rid_missing", "Reset request ID missing. Please use the link from your email."));
    if (code.length !== OTP_LEN || otp.some((d) => d === "")) return setError(t("error_code_incomplete", "The code must be 6 digits."));
    if (pw1.length < 8) return setError(t("error_password_too_short", "Password must be at least 8 characters."));
    if (pw1 !== pw2) return setError(t("error_passwords_mismatch", "Passwords do not match."));

    setLoading(true);
    try {
      const res = await fetch(apiPath("/api/auth/password/reset/confirm"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        cache: "no-store",
        body: JSON.stringify({ rid, code, newPassword: pw1 }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok) {
        setError(json?.message ?? t("error_reset_failed", "Password reset failed."));
        return;
      }

      setDone(true);
    } catch {
      setError(t("error_network", "Network error."));
    } finally {
      setLoading(false);
    }
  }

  if (done) {
    return (
      <Section>
        <Wrapper>
          <div className="mx-auto max-w-lg">
            <div className="flex flex-col gap-6">
              <h1 className="text-title-1 font-semibold">
                {t("done_heading", "Password updated")}
              </h1>
              <p className="m-0 text-body-2">
                {t("done_message", "You can now sign in with your new password.")}
              </p>
              <Button
                type="button"
                className="group w-full"
                center
                onClick={() => {
                  router.replace(loginHref);
                  router.refresh();
                }}
              >
                <SafeHtml content={t("done_go_to_login_button", "Go to sign in")} />
              </Button>
            </div>
          </div>
        </Wrapper>
      </Section>
    );
  }

  return (
    <Section>
      <Wrapper>
        <div className="mx-auto max-w-lg">
          <div className="flex flex-col gap-6">
            <h1 className="text-title-1 font-semibold">
              {t("heading", "Recover password")}
            </h1>

            <p className="m-0 text-body-2">
              {t("intro", "Use the link from your email and then enter the 6-digit code.")}
            </p>

            {!rid && (
              <p className="m-0 text-body-2 text-fuji-red">
                <SafeHtml content={t("rid_missing_notice_html", "<code>rid</code> missing from URL. Please use the reset link from your email.")} />
              </p>
            )}

            {step === "code" ? (
              <form onSubmit={goNextFromCode} noValidate className="flex flex-col gap-6">
                <OtpInput
                  idPrefix="reset-otp"
                  label={t("otp_label", "Verification code")}
                  value={otp}
                  onChange={setOtp}
                  invalid={!!codeError}
                  error={codeError}
                  onClearError={() => setCodeError(null)}
                />

                <div className="flex w-full flex-col gap-3">
                  <Button
                    type="submit"
                    className="group w-full"
                    center
                    disabled={!rid || otp.some((d) => d === "")}
                  >
                    <SafeHtml content={t("continue_button", "Continue")} />
                  </Button>

                  <button
                    type="button"
                    className="text-body-2 underline text-left"
                    onClick={() => router.replace(loginHref)}
                  >
                    {t("back_to_login_link", "Back to sign in")}
                  </button>
                </div>
              </form>
            ) : (
              <form onSubmit={submitPassword} noValidate className="flex flex-col gap-6">
                <div className="w-full min-w-0">
                  <Input
                    id="reset-new-password"
                    label={<SafeHtml content={t("new_password_label", "New password")} />}
                    type="password"
                    value={pw1}
                    onChange={(e) => setPw1(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>

                <div className="w-full min-w-0">
                  <Input
                    id="reset-confirm-password"
                    label={<SafeHtml content={t("confirm_password_label", "Confirm new password")} />}
                    type="password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    autoComplete="new-password"
                    required
                  />
                </div>

                {error && <p className="m-0 text-body-2 text-fuji-red">{error}</p>}

                <div className="flex w-full flex-col gap-3">
                  <Button
                    type="submit"
                    className="group w-full"
                    center
                    disabled={loading}
                    loading={loading}
                  >
                    <SafeHtml
                      content={
                        loading
                          ? t("updating_button", "Updating…")
                          : t("update_button", "Update password")
                      }
                    />
                  </Button>

                  <div className="flex items-center justify-between text-body-2">
                    <button
                      type="button"
                      className={`underline ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                      onClick={() => {
                        clearOtp();
                        setStep("code");
                      }}
                      disabled={loading}
                    >
                      {t("back_button", "Back")}
                    </button>

                    <button
                      type="button"
                      className={`underline ${loading ? "opacity-60 cursor-not-allowed" : ""}`}
                      onClick={() => router.replace(loginHref)}
                      disabled={loading}
                    >
                      {t("back_to_login_link", "Back to sign in")}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </Wrapper>
    </Section>
  );
}